import {
  Component,
  inject,
  input,
  InputSignal,
  ChangeDetectionStrategy,
  computed,
  Signal,
  effect,
} from '@angular/core';
import { QuestionStep } from '../models/step/question-step.model';
import { Step } from '../models/step/step.model';
import { ThankYouStep } from '../models/step/thank-you-step.model';
import { ImagePositionOnPage } from '../shared/enums/image-position-on-page.enum';
import { StepSupplierService } from './step-supplier.service';
import { ImageLeftOfQuestionComponent } from './question/templates/image-left-of-question/image-left-of-question.component';
import { ImageInSidePaneComponent } from './question/templates/image-in-side-pane/image-in-side-pane.component';
import { ImageBeneathQuestionComponent } from './question/templates/image-beneath-question/image-beneath-question.component';
import { ImagesInSlidesComponent } from './question/templates/images-in-slides/images-in-slides.component';
import { NoImageComponent } from './question/templates/no-image/no-image.component';
import { ThankYouComponent } from './thank-you/thank-you.component';

/**
 * This component handles displaying the right question type.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-step-supplier',
  templateUrl: './step-supplier.component.html',
  styleUrls: ['./step-supplier.component.scss'],
  imports: [
    ImageLeftOfQuestionComponent,
    ImageInSidePaneComponent,
    ImageBeneathQuestionComponent,
    ImagesInSlidesComponent,
    NoImageComponent,
    ThankYouComponent,
  ],
  providers: [StepSupplierService],
})
export class StepSupplierComponent {
  private readonly stepSupplierService: StepSupplierService =
    inject(StepSupplierService);

  /**
   * Flag to determine to display the placeholder.
   */
  public readonly isFetching: InputSignal<boolean> = input<boolean>(false);

  /**
   * The current session id.
   */
  public readonly sessionId: InputSignal<string | undefined> = input<
    string | undefined
  >();

  public readonly step: InputSignal<Step | undefined> = input<
    Step | undefined
  >();

  /**
   * Expose the ImagePositionOnPage enum to the template.
   */
  public imagePositionOnPage: typeof ImagePositionOnPage = ImagePositionOnPage;

  /**
   * Method used in template to check if the current step is a QuestionStep.
   */
  public readonly isQuestionStep: Signal<QuestionStep | undefined> = computed(
    () => {
      const step: Step | undefined = this.step();
      return step instanceof QuestionStep ? step : undefined;
    },
  );

  /**
   * Method used in template to check if the current step is a QuestionStep
   */
  public readonly isThankYouStep: Signal<ThankYouStep | undefined> = computed(
    () => {
      const step: Step | undefined = this.step();
      return step instanceof ThankYouStep ? step : undefined;
    },
  );

  constructor() {
    effect(() => {
      const sessionId: string | undefined = this.sessionId();
      if (sessionId !== undefined) {
        this.stepSupplierService.setSessionId(sessionId);
      }
    });
  }
}
